import { useEffect } from 'react'
import { Grid } from '@mui/material'
import { useField, useFormikContext } from 'formik'
import { useTranslation } from 'react-i18next'

import {
  DatePickerField,
  InputField,
  TimePickerField,
  AutoCompleteField,
  TextareaField
} from 'src/components/shared/ui/form/fields'
import FieldsetGroup from 'src/components/shared/ui/form/fields/FieldsetGroup/FieldsetGroup'
import useNumberValidations from 'src/components/shared/ui/form/fields/hooks/useNumberValidations'
import DatosSwitches from 'src/components/views/EscritosLexnet/components/IniciadorEjecucion/components/Datos/DatosSwitches'
import MateriaOrDelitoAutocomplete from 'src/components/views/EscritosLexnet/components/shared/components/iniciadores/MateriaOrDelitoAutocomplete'
import { EscritosFormValues } from 'src/components/views/EscritosLexnet/components/shared/types/escritosFormTypes'
import { Orden as OrdenMaster, TipoCuantiaOpciones } from 'src/enums/EscritosLegales'
import { FormField } from 'src/enums/Form'
import { useGetEntidadesByOrganismoOrigen } from 'src/hooks/fetch/useCatalogoGeneralFilters'
import { Orden, TipoCuantia } from 'src/interfaces/Catalog'

function Datos(): JSX.Element {
  const { t } = useTranslation()
  const { setFieldValue, values, validateForm, resetForm } = useFormikContext<EscritosFormValues>()
  const { allowFloatNumber } = useNumberValidations()
  const [orden] = useField<Orden>(
    `${FormField.ORGANO_DESTINO}.${FormField.ORGANO_DESTINO_ORDEN_JUDICIAL}`
  )
  const organismoOrigenData = useGetEntidadesByOrganismoOrigen(FormField.DATOS_ORGANISMO_ORIGEN)

  const datosTipoCuantia: TipoCuantia | null = values[FormField.DATOS_TIPO_CUANTIA]
  const datosNumeroExpedienteOrigen = values[FormField.DATOS_NUMERO_EXPEDIENTE_ORIGEN]
  const datosYearExpedienteOrigen = values[FormField.DATOS_YEAR_EXPEDIENTE_ORIGEN]
  const deTituloJucial = values[FormField.ORGANO_DESTINO_DE_TITULO_JUDICIAL]

  const { allowInteger } = useNumberValidations()

  useEffect(() => {
    if (deTituloJucial) {
      setFieldValue(FormField.DATOS_ORGANISMO_ORIGEN, null)
      setFieldValue(FormField.DATOS_NUMERO_EXPEDIENTE_ORIGEN, '', false)
      setFieldValue(FormField.DATOS_YEAR_EXPEDIENTE_ORIGEN, '', false)
    }
  }, [deTituloJucial, setFieldValue, validateForm])

  function showCuantiaFields(): JSX.Element | null {
    const options = [OrdenMaster.CIVIL, OrdenMaster.CONTENCIOSO, OrdenMaster.SOCIAL]
    if (orden.value && options.includes(orden.value.orden)) {
      return (
        <>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <AutoCompleteField
                id={FormField.DATOS_TIPO_CUANTIA}
                items={[
                  { codigo: 'N', descripcion: TipoCuantiaOpciones.NO_PROCEDE },
                  { codigo: 'I', descripcion: TipoCuantiaOpciones.INDETERMINADA },
                  { codigo: 'D', descripcion: TipoCuantiaOpciones.DETERMINADA }
                ]}
                label={t('form.field.datos_tipo_cuantia')}
                itemName="descripcion"
                itemKey="codigo"
                required
              />
            </Grid>
            {datosTipoCuantia && datosTipoCuantia.codigo === 'D' && (
              <Grid item xs={12} sm={6}>
                <InputField
                  id={FormField.DATOS_CUANTIA_PRINCIPAL}
                  label={t('form.field.datos_cuantia_principal')}
                  clearable
                  currencyType
                  required
                  onChange={allowFloatNumber}
                  maxLength={16}
                />
              </Grid>
            )}
          </Grid>
          {datosTipoCuantia && datosTipoCuantia.codigo === 'D' && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <InputField
                  id={FormField.DATOS_CUANTIA_INTERESES}
                  label={t('form.field.datos_cuantia_intereses')}
                  clearable
                  onChange={allowFloatNumber}
                  maxLength={10}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <InputField
                  id={FormField.DATOS_CUANTIA_COSTAS}
                  label={t('form.field.datos_cuantia_costas')}
                  clearable
                  onChange={allowFloatNumber}
                  maxLength={10}
                />
              </Grid>
            </Grid>
          )}
        </>
      )
    }
    return null
  }

  function showOtherPenalFields(): JSX.Element | null {
    if (orden.value && orden.value.orden === OrdenMaster.PENAL) {
      return (
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <InputField
              id={FormField.DATOS_LOCALIDAD}
              label={t('form.field.localidad')}
              clearable
              maxLength={200}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <DatePickerField id={FormField.DATOS_FECHA} label={t('form.field.fecha')} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TimePickerField id={FormField.DATOS_HORA} label={t('form.field.hora')} />
          </Grid>
        </Grid>
      )
    }
    return null
  }

  function showNoDeTituloJudicalFields(): JSX.Element | null {
    if (!deTituloJucial) {
      if (orden.value && orden.value.orden === OrdenMaster.CIVIL) {
        return (
          <InputField
            id={FormField.DATOS_ORGANISMO_ORIGEN}
            label={t('form.field.datos_organismo_origen')}
            clearable
            maxLength={200}
          />
        )
      }
      if (orden.value && orden.value.orden === OrdenMaster.SOCIAL) {
        return (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <AutoCompleteField
                id={FormField.DATOS_ORGANISMO_ORIGEN}
                items={organismoOrigenData}
                label={t('form.field.datos_organismo_origen')}
                required
                itemName="descripcion"
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <InputField
                id={FormField.DATOS_NUMERO_EXPEDIENTE_ORIGEN}
                label={t('form.field.datos_numero_expediente_origen')}
                maxLength={21}
                onChange={allowInteger}
                clearable
                required={Boolean(datosNumeroExpedienteOrigen || datosYearExpedienteOrigen)}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <InputField
                id={FormField.DATOS_YEAR_EXPEDIENTE_ORIGEN}
                label={t('form.field.datos_year_expediente_origen')}
                maxLength={4}
                onChange={allowInteger}
                clearable
                required={Boolean(datosNumeroExpedienteOrigen || datosYearExpedienteOrigen)}
              />
            </Grid>
          </Grid>
        )
      }
    }
    return null
  }

  useEffect(() => {
    if (datosTipoCuantia?.descripcion !== TipoCuantiaOpciones.DETERMINADA) {
      resetForm({
        values: {
          ...values,
          [FormField.DATOS_CUANTIA_PRINCIPAL]: '',
          [FormField.DATOS_CUANTIA_INTERESES]: '0',
          [FormField.DATOS_CUANTIA_COSTAS]: '0'
        }
      })
    }
    // eslint-disable-next-line
  }, [datosTipoCuantia])

  return (
    <FieldsetGroup legend={t('form.legend.datos')}>
      <MateriaOrDelitoAutocomplete />
      {showCuantiaFields()}
      {showOtherPenalFields()}
      {showNoDeTituloJudicalFields()}
      <TextareaField
        id={FormField.DATOS_OBSERVACIONES}
        label={t('form.field.observaciones')}
        maxLength={1000}
        disabled={!orden.value}
      />
      <DatosSwitches />
    </FieldsetGroup>
  )
}

export default Datos
